import { useEffect, useState } from "react";
import { X, Zap, BatteryMedium, Satellite, SignalHigh, Loader2, LineChart as LineChartIcon, Gauge, Fuel, Thermometer, Route } from "lucide-react";
import BrandLoader from "@/components/BrandLoader";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { limitDays } from "@/lib/reportLimit";

const METRICS = {
  voltage: { label: "Alimentation", unit: "V", color: "#17A2B8", icon: Zap },
  battery: { label: "Batterie", unit: "V", color: "#10B981", icon: BatteryMedium },
  satellites: { label: "Satellites", unit: "", color: "#F5821F", icon: Satellite },
  gsm: { label: "Signal GSM", unit: "/5", color: "#A855F7", icon: SignalHigh },
  rpm: { label: "Régime", unit: " tr/min", color: "#0EA5E9", icon: Gauge },
  fuel_pct: { label: "Carburant", unit: " %", color: "#22C55E", icon: Fuel },
  coolant_temp: { label: "Température", unit: " °C", color: "#EF4444", icon: Thermometer },
  can_speed: { label: "Vitesse CAN", unit: " km/h", color: "#F59E0B", icon: Gauge },
  total_mileage_km: { label: "Km total", unit: " km", color: "#8B5CF6", icon: Route },
  fuel_l: { label: "Carburant (L)", unit: " L", color: "#14B8A6", icon: Fuel },
  throttle: { label: "Accélérateur", unit: " %", color: "#EAB308", icon: Gauge },
};
const BASE_KEYS = ["voltage", "battery", "satellites", "gsm"];
const PERIODS = [{ h: 24, label: "24 h" }, { h: 168, label: "7 j" }, { h: 720, label: "30 j" }];

export default function SensorChartModal({ vehicle, metric: initialMetric, onClose }) {
  const [metric, setMetric] = useState(initialMetric || "voltage");
  const { user } = useAuth();
  const lim = limitDays(user);
  const [hours, setHours] = useState(24);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/vehicles/${vehicle.id}/telemetry-series?hours=${hours}`)
      .then((r) => { if (!cancel) setSeries(r.data.series || []); })
      .catch(() => { if (!cancel) setSeries([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [vehicle.id, hours]);

  const meta = METRICS[metric] || METRICS.voltage;
  const hasData = (k) => (series || []).some((p) => p[k] != null);
  const fmtX = (t) => {
    try {
      const d = new Date(t);
      return hours <= 24
        ? d.toLocaleTimeString("fr-FR", { timeZone: "Pacific/Noumea", hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit" });
    } catch { return ""; }
  };
  const points = (series || []).filter((p) => p[metric] != null).map((p) => ({ t: p.t, val: p[metric] }));

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="sensor-chart-modal">
      <div className="glass rounded-xl w-full max-w-2xl p-5 animate-slidein border border-[#17A2B8]/40" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <LineChartIcon className="w-5 h-5 brand-teal shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-base truncate">{vehicle.name} <span className="data text-xs text-muted-foreground">· {vehicle.plate}</span></p>
              <p className="label-mono">Historique des capteurs</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="sensor-chart-close" className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex flex-wrap gap-1.5" data-testid="sensor-metric-tabs">
            {Object.entries(METRICS).filter(([k]) => BASE_KEYS.includes(k) || hasData(k)).map(([k, m]) => {
              const Icon = m.icon;
              const on = k === metric;
              return (
                <button key={k} onClick={() => setMetric(k)} data-testid={`sensor-tab-${k}`}
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors"
                  style={{ color: on ? "#fff" : m.color, background: on ? m.color : `${m.color}1f`, borderColor: `${m.color}66` }}>
                  <Icon className="w-3 h-3" /> {m.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-1" data-testid="sensor-period-tabs">
            {PERIODS.filter((p) => !lim || p.h <= lim * 24).map((p) => (
              <button key={p.h} onClick={() => setHours(p.h)} data-testid={`sensor-period-${p.h}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${hours === p.h ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-72 w-full" data-testid="sensor-chart-canvas">
          {loading ? (
            <div className="h-full flex items-center justify-center"><BrandLoader size={44} rings={false} label="Chargement des capteurs…" /></div>
          ) : points.length < 2 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm" data-testid="sensor-chart-empty">Pas assez de données sur la période pour « {meta.label} ».</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize: 11, fill: "#a1a1aa" }} minTickGap={40} />
                <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} width={44} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                  labelFormatter={(t) => new Date(t).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea" })}
                  formatter={(v) => [`${v}${meta.unit}`, meta.label]} />
                <Line type="monotone" dataKey="val" stroke={meta.color} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 text-right">{points.length} point(s) · heure de Nouméa</p>
      </div>
    </div>
  );
}
